import { DateTime } from 'luxon';
import { FilterCondition, FilterDTO, FilterOperator } from '../models/models.swagger.generated';
import { DateHelper } from './date-helper';

export const LICENSE_PLATE_MIN_LENGTH = 2;
export const LICENSE_PLATE_FIELDS = [
  'KnownTag.ControlPlateNumber',
  'KnownTag.OperationalControlPlate',
  'UnknownParkerTagOperationalControlPlateNumber',
  'UnknownParkerTagControlPlateNumber',
];

export class FilterUtil {
  public static FindAllFiltersWithSpecificField(fieldName: string, filters: FilterDTO[]): FilterDTO[] {
    const resFilters: FilterDTO[] = [];
    if (filters) {
      filters.forEach((f) => {
        const subFilters = FilterUtil.FindAllFiltersWithSpecificFieldInFilter(fieldName, f);
        if (subFilters.length) {
          resFilters.push(...subFilters);
        }
      });
    }
    return resFilters;
  }

  public static FindAllFiltersWithSpecificFieldInFilter(fieldName: string, filter: FilterDTO): FilterDTO[] {
    const filters: FilterDTO[] = [];
    if (filter.isComplex) {
      if (filter.predicates) {
        const subFilters = FilterUtil.FindAllFiltersWithSpecificField(fieldName, filter.predicates);
        if (subFilters.length) {
          filters.push(...subFilters);
        }
      }
    } else if (filter.field?.toLocaleLowerCase() === fieldName.toLocaleLowerCase()){
      filters.push(filter);
    }
    return filters;
  }

  public static correctDateTimeFilters(filters: FilterDTO[], fieldName: string, timeZone: string | luxon.Zone) {
    if (!filters) {
      return;
    }
    const filtersToCorrect = FilterUtil.FindAllFiltersWithSpecificField(fieldName, filters);
    filtersToCorrect.forEach(f => {
      if (f.value) {
        f.value = DateHelper.migrateTimeZone(DateTime.fromISO(f.value), timeZone);
      }
    });
  }

  public static replaceFieldNameInfilters(filters: FilterDTO[], oldFieldName: string, newFieldName: string) {
    if (!filters) {
      return;
    }
    const filtersToCorrect = FilterUtil.FindAllFiltersWithSpecificField(oldFieldName, filters);
    filtersToCorrect.forEach(f => {
      f.field = newFieldName;
    });
  }

  // extends a simple filter with a specific field to a complex filter with OR condition and multiple fields (same operator and value)
  public static extendFiltersToMultiFieldFilter(filters: FilterDTO[], fieldName: string, newFieldNames: string[], filterCondition: FilterCondition): void {
    const filtersToAdjust = FilterUtil.FindAllFiltersWithSpecificField(fieldName, filters);
    filtersToAdjust.forEach(f => {
      f.isComplex = true;
      f.condition = filterCondition;
      f.predicates = newFieldNames.map(newFieldName => new FilterDTO({
        isComplex: false,
        field: newFieldName,
        operator: f.operator,
        value: f.value
      }));
      f.operator = undefined;
      f.value = undefined;
    });
  }

  public static addParkerNameFilter(filters: FilterDTO[], parkerName: string | null): void {
    if (parkerName) {
      filters.push(new FilterDTO({
        isComplex: false,
        field: 'FullName',
        operator: FilterOperator.StartsWith,
        value: parkerName,
      }));
    }
  }

  public static addLicensePlateFilter(filters: FilterDTO[], licensePlate: string | null): void {
    if (!licensePlate?.trim()) {
      return;
    }

    const plates = licensePlate
      .split(/[,;]/)
      .map(p => p.trim())
      .filter(p => p.length >= LICENSE_PLATE_MIN_LENGTH);

    if (plates.length === 0) {
      return;
    }

    const platePredicates = plates.flatMap(plate =>
      LICENSE_PLATE_FIELDS.map(field =>
        new FilterDTO({ isComplex: false, field, operator: FilterOperator.Contains, value: plate })
      )
    );

    filters.push(new FilterDTO({
      isComplex: true,
      condition: FilterCondition.Or,
      predicates: platePredicates,
    }));
  }
}
